import { AxiosError } from "axios";
import { useSetAtom } from "jotai";
import { FormEvent, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ButtonLink, SubmitButton } from "@/components/Interface/Button/Button";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { OrSeparator } from "@/components/Layout/OrSeparator";
import {
  stepDescriptionClassName,
  stepHeadingClassName,
} from "@/components/Portal/UserAccount/Security/AuthenticatorSetup/authenticator.utils";
import { AuthenticatorPhoneNumberField } from "@/components/Portal/UserAccount/Security/AuthenticatorSetup/AuthenticatorPhoneNumberField";
import { switch2faRouteByMethod } from "@/components/Portal/UserAccount/Security/utils";
import { useAlert } from "@/hooks/useAlert";
import { NumberForm } from "@/hooks/useNumberForm";
import { useRequestTwoFactorPhoneNumber } from "@/hooks/useRequestTwoFactorPhoneNumber";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useChangeTwoFactorAuthenticationSMSNumber } from "@/services/auth";
import { ChangeTwoFactorAuthenticationMethod } from "@/services/model";
import { AlertTypes, phoneNumberAtom } from "@/utils/atoms";

import { TwoFactorAlternatives } from "./TwoFactorAlternatives";

/**
 * 2FA settings card for SMS users: change the 2FA phone number, or switch to
 * another 2FA method. Both are alternatives, so filling one clears the other.
 */
export const TwoFactorSmsPhonenumberChange = () => {
  const navigate = useNavigate();
  const showToast = useAlert();
  const { t } = useTranslation();
  const {
    control,
    setValue,
    setError,
    clearErrors,
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<NumberForm>({
    defaultValues: {
      number: "",
    },
    mode: "onChange",
  });
  const newPhoneNumber = useWatch({ control, name: "number" });
  const [method, setMethod] =
    useState<ChangeTwoFactorAuthenticationMethod | null>(null);

  useEffect(() => {
    if (newPhoneNumber) {
      setMethod(null);
    }
  }, [newPhoneNumber]);

  const setPhoneNumber = useSetAtom(phoneNumberAtom);
  const { mutateAsync: changePhoneNumber } =
    useChangeTwoFactorAuthenticationSMSNumber();

  const phoneNumber = useRequestTwoFactorPhoneNumber();

  const handlePhonenumerChangeRejection = (error: AxiosError<string>) => {
    // If we get a 403 error, we know that the phone number is invalid
    if (error.response?.data.includes("invalid phone number")) {
      setError("number", {
        type: "manual",
        message: t("translation:label.validation.phoneBackend"),
      });
    } else {
      showToast({
        text: t("portal:user-account.security.2fa.changePhonenumber.error"),
        type: AlertTypes.error,
      });
    }
  };

  const onSubmitPhoneNumber = (data: NumberForm) => {
    changePhoneNumber({
      params: {
        phoneNumber: data.number,
      },
    })
      .then(() => {
        setPhoneNumber(data.number);
        navigate(portalRoutes.user.security.verify2faNumberChange);
      })
      .catch(handlePhonenumerChangeRejection);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    // Without a picked alternative, the form changes the SMS number.
    const nextStep = method && switch2faRouteByMethod[method];

    if (nextStep) {
      event.preventDefault();
      navigate(nextStep);

      return;
    }

    handleSubmit(onSubmitPhoneNumber)(event);
  };

  const selectMethod = (selected: ChangeTwoFactorAuthenticationMethod) => {
    setMethod(selected);
    setValue("number", "");
    clearErrors("number");
  };

  return (
    <form onSubmit={onSubmit}>
      <CardModule
        stickyHeader
        title={t("portal:user-account.security.2fa.initialSetup.title")}
        actions={
          <>
            <ButtonLink
              variant="secondary"
              size="sm"
              to={portalRoutes.user.security.base}
            >
              {t("portal:user-account.security.buttons.back")}
            </ButtonLink>
            <SubmitButton
              fullWidth={false}
              size="sm"
              disabled={!newPhoneNumber && !method}
              label={t("Common.label.finish")}
            />
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <h3 className={stepHeadingClassName}>
            {t("portal:user-account.security.2fa.changePhonenumber.title")}
          </h3>
          <p className={stepDescriptionClassName}>
            <Trans
              t={t}
              i18nKey="portal:user-account.security.2fa.changePhonenumber.text"
              values={{
                phoneNumber: phoneNumber?.replace(/^00/, "+"),
              }}
              components={[<strong key="0" className="font-semibold" />]}
            />
          </p>
          <AuthenticatorPhoneNumberField register={register} errors={errors} />
        </div>
        <OrSeparator />
        <TwoFactorAlternatives
          currentMethod={ChangeTwoFactorAuthenticationMethod.SMS}
          value={method}
          onChange={selectMethod}
        />
      </CardModule>
    </form>
  );
};
